import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSeo, breadcrumb, productJsonLd } from "@/lib/seo";
import { IMAGES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { MateriauxLigne } from "@/components/MateriauxOrigine";
import { ProductCard } from "@/components/ProductCard";
import { Marquee } from "@/components/Marquee";
import { useLocale } from "@/context/LocaleContext";

const FILTRES_PEAU = ["Toutes", "Chèvre", "Cheval", "Cerf", "Bison", "Chamois", "Lama"];

const Boutique = () => {
  const { locale, t } = useLocale();
  const [products, setProducts] = useState(null);
  const [peau, setPeau] = useState("Toutes");
  const [dispoSeulement, setDispoSeulement] = useState(false);

  useEffect(() => {
    api.products().then(setProducts).catch(() => setProducts([]));
  }, [locale]);
  useSeo({
    title: locale === "en" ? "Handcrafted drums made in Switzerland — Entre Les Mondes shop" : "Tambours artisanaux faits main en Suisse — Boutique Entre les Mondes",
    description: locale === "en" ? "Traditional drums handcrafted in Orbe: goat, horse, deer and bison skins. Each piece is unique, tuned and ready to play. Delivery in Switzerland and Europe." : "Tambours chamaniques et traditionnels créés à la main à Orbe : peaux de chèvre, cheval, cerf, bison… Chaque pièce est unique, accordée et prête à jouer. Livraison en Suisse et en Europe.",
    image: IMAGES.mains,
    jsonLd: products?.length ? [breadcrumb([["Accueil", "/"], ["Boutique", "/boutique"]]), { "@type": "ItemList", itemListElement: products.map((p, i) => ({ "@type": "ListItem", position: i + 1, item: productJsonLd(p) })) }] : null,
  });

  const filtres = (products || []).filter(
    (p) => (peau === "Toutes" || p.peau === peau) && (!dispoSeulement || p.disponible)
  );

  return (
    <div>
      <section className="bg-earth grain text-sand relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-44 pb-20 relative z-[2]">
          <Reveal>
            <p className="eyebrow mb-6 text-sand/70">{t("La boutique — pièces uniques")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
              {t("Des tambours façonnés main, un seul exemplaire de chaque.")}
            </h1>
            <p className="mt-6 max-w-2xl text-sand/70 leading-relaxed">
              {t("Chaque tambour est une pièce unique — par sa peau, son cadre, son laçage et sa sonorité. Mailloches artisanales en poils disponibles. Livraison offerte en Suisse, expédition soignée dans toute l'Europe francophone.")}
            </p>
            <MateriauxLigne dark className="mt-6 max-w-2xl" testid="materiaux-ligne-boutique" />
          </Reveal>
        </div>
      </section>
      <Marquee />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="shop-grid-section">
        <Reveal className="flex flex-wrap items-center gap-4 mb-12">
          <div className="flex flex-wrap gap-2" role="group" aria-label={t("Filtrer par peau")}>
            {FILTRES_PEAU.map((f) => (
              <button key={f} onClick={() => setPeau(f)} data-testid={`filter-peau-${f.toLowerCase()}`}
                className={`px-5 py-2.5 rounded-full text-sm transition-colors duration-300 ${
                  peau === f ? "bg-earth text-sand" : "border border-wood/25 text-fumee hover:border-terra hover:text-terra"
                }`}>
                {f === "Toutes" ? t("Toutes les peaux") : `${t("Peau de")} ${locale === "en" ? t(f) : f.toLowerCase()}`}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-3 text-sm text-fumee cursor-pointer select-none ml-auto">
            <input type="checkbox" data-testid="filter-disponible" checked={dispoSeulement}
              onChange={(e) => setDispoSeulement(e.target.checked)}
              className="w-4 h-4 accent-[#AE9A64]" />
            {t("Disponibles uniquement")}
          </label>
        </Reveal>

        {!products ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-96 rounded-2xl bg-sand-dark/60 animate-pulse" />)}
          </div>
        ) : filtres.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtres.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        ) : (
          <p className="text-fumee py-16 text-center font-serif text-2xl" data-testid="shop-empty">
            {t("Aucun tambour ne correspond — élargissez votre écoute.")}
          </p>
        )}
      </section>
    </div>
  );
};

export default Boutique;
